import { Button, ModalV2 } from '@components'
import { useBusinessContext, useFlag, useFlagsLoaded } from '@context'
import { BusinessForms, BusinessOnboardingStepKey } from '@query'
import { completeOnboardStep } from '@query/onboard'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { FaExternalLinkAlt, FaFileInvoice } from 'react-icons/fa'
import {
  AccrualChoice,
  AccountingMethodSelector,
  getRecommendedAccrualChoice
} from './AccountingMethodSelector'
import {
  DocsLink,
  FooterActions,
  HeaderTile,
  HeaderTitle,
  ModalBody
} from './AccrualAccountingModal.styled'

interface Props {
  isVisible: boolean
  stepId: number
  onClose: () => void
  onResolved: (groupKey: BusinessOnboardingStepKey, kind: 'done' | 'skipped') => void
}

interface AccrualAccountingFormValues {
  choice: AccrualChoice
}

const FORM_ID = 'accrual-accounting-form'

export const AccrualAccountingModal: React.FC<Props> = ({
  isVisible,
  stepId,
  onClose,
  onResolved
}) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const flagsLoaded = useFlagsLoaded()
  const workflowsFlag = useFlag('workflows')
  const workflowsHubEnabled = flagsLoaded && workflowsFlag.enabled
  const defaultChoice: AccrualChoice = business?.has_accrual_based_entries_for_invoicing
    ? 'yes'
    : 'no'
  const recommended = getRecommendedAccrualChoice(business?.form)

  const { mutateAsync: submitChoice, isPending: isSubmitting } = useMutation({
    mutationFn: ({ choice }: AccrualAccountingFormValues) =>
      completeOnboardStep({
        slug,
        stepId,
        has_accrual_based_entries_for_invoicing: choice === 'yes'
      }),
    onError: () => toast.error(t('general.error')),
    onSuccess: async () => {
      onResolved(BusinessOnboardingStepKey.ACCRUAL_ACCOUNTING, 'done')
      await queryClient.refetchQueries({ queryKey: [slug] })
      onClose()
    }
  })

  const introText = workflowsHubEnabled
    ? t('dashboard.getStartedWidget.accrualAccounting.descriptionHub')
    : t('dashboard.getStartedWidget.accrualAccounting.description')

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={
        <HeaderTitle>
          <HeaderTile>
            <FaFileInvoice />
          </HeaderTile>
          {t('dashboard.getStartedWidget.accrualAccounting.title')}
        </HeaderTitle>
      }
      width={720}
      height={900}
      autoHeight
      footer={
        <>
          <DocsLink
            href={t('link.docs.accrualBasedInvoicing')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dashboard.getStartedWidget.accrualAccounting.readMore')}
            <FaExternalLinkAlt />
          </DocsLink>
          <FooterActions>
            <Button type="button" isSecondary onClick={onClose} disabled={isSubmitting}>
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              intent="primary"
              showSpinner={isSubmitting}
              disabled={isSubmitting}
            >
              {t('dashboard.getStartedWidget.accrualAccounting.submit')}
            </Button>
          </FooterActions>
        </>
      }
    >
      {isVisible && (
        <AccrualAccountingForm
          defaultChoice={defaultChoice}
          recommended={recommended}
          businessForm={business?.form}
          introText={introText}
          onSubmit={submitChoice}
        />
      )}
    </ModalV2>
  )
}

const AccrualAccountingForm: React.FC<{
  defaultChoice: AccrualChoice
  recommended: AccrualChoice | null
  businessForm?: BusinessForms
  introText: string
  onSubmit: (values: AccrualAccountingFormValues) => void | Promise<void>
}> = ({ defaultChoice, recommended, businessForm, introText, onSubmit }) => {
  const formValues = React.useMemo(() => ({ choice: defaultChoice }), [defaultChoice])
  const { control, handleSubmit } = useForm<AccrualAccountingFormValues>({
    values: formValues
  })

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="choice"
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <ModalBody>
            <AccountingMethodSelector
              value={value}
              onChange={onChange}
              businessForm={businessForm}
              recommended={recommended}
              showIntro
              introText={introText}
            />
          </ModalBody>
        )}
      />
    </form>
  )
}
